<!--
File: PlanDetails.vue
Description: show list of Routine Maintenance Plan details.
-->
<template>
  <base-data-table
    v-if="eligible"
    :loading="showSpinner"
    :pagination="pagination"
    :from="from"
    :to="to"
    :total="total"
    @add-item="viewItem()">

    <template #table-rows="{ item }">
      <base-table
        :items="queriedData"
        :headers="headers"
        :idFieldName="idFieldName"
        :sort="currentSort"
        :sortOrder="currentSortOrder"
        :showActions="true">

        <template #table-actions="{ item }">
          <table-actions v-if="!planApproved"
            :btnExtraAllowed="true"
            :extraButtonClass="'md-just-icon md-success md-simple'"
            :btnExtraAsIcon="true"
            :extraButtonIcon="'content_copy'"
            :btnExtraStyle="true"
            :extraButtonLabel="$t('buttons.copy')"
            @extraButtonClicked="copyItem(item)"

            :btnEditAllowed="isBtnAllowed('EditButton')"
            :btnDeleteAllowed="isBtnAllowed('DeleteButton')"
            @edit="viewItem(item[idFieldName])"
            @delete="deleteItem(item[idFieldName], item.description)" 
          />
        </template>
      </base-table>

      <div class="footer-table md-table">
        <table>
          <tfoot>
            <tr>
              <th v-for="item in footerTable" :key="item" class="md-table-head">
                <div class="md-table-head-container md-ripple md-disabled">
                  <div class="md-table-head-label">{{ item }}</div>
                </div>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>

    <template #editing-dialog>
      <EditForm
        v-if="showDetailsDlg"
        ref="dynamicForm"
        :formName="formName"
        :actionBase="'rm_plan_detail'"
        :idFieldName="idFieldName"
        :moduleName="'Planning'"
        :itemId="currentId"
        :fields="fields"
        :validations="validations"
        :messageTitle="$t('tabs.plan_details')"
        @close="showDetailsDlg = false">
      </EditForm>
    </template>
  </base-data-table>
</template>
<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { BaseDataTable, BaseTable, TableActions } from '@/pages/Components';
import BaseTableMixin from '@/mixins/BaseTableMixin';
import { numFormat } from "@/store/helpers/format_helpers"
import { savedMessage } from '@/mixins/messagesMixin';
import { EditForm } from '@/pages/Components'

export default {
  name: 'test-rm-plan-details',
  mixins: [BaseTableMixin],

  components: {
    BaseDataTable,
    BaseTable,
    TableActions,
    EditForm
  },

  data() {
    return {
      formName: 'PlanDetails',
      idFieldName: 'routine_plan_details_id',
      currentSort: 'total_cost',

      payload: { fk_routine_plan: -1 },
      selectedYear: null,
      selectedRoad: -1,

      historyMapping: {
        selectedRegion: 0,
        currentSort: 'total_cost',
        currentSortOrder: 'asc',
        pagination: this.pagination,
        searchQuery: ''
      }
    }
  },

  props: {
    step: { default: '', type: String },
    filterParams: { default: {}, type: Object },
  },

  async mounted() {
    this.payload.fk_work_category = -1;
    await this.loadPlans({});
  },

  methods: {
    ...mapActions({
      loadPlans: 'Planning/LOAD_RM_PLAN_LIST',
      clearList: 'Planning/CLEAR_RM_PLAN_DETAIL_LIST',
      loadList: 'Planning/LOAD_RM_PLAN_DETAIL_LIST',
      addItem: 'Planning/ADD_NEW_RM_PLAN_DETAIL',
      delete: 'Planning/DELETE_RM_PLAN_DETAIL',
      approvePlan: 'Planning/APPROVE_RM_PLAN',
      cancelPlan: 'Planning/CANCEL_RM_PLAN',
    }),

    async parseFilterParams() {
      await this.clearList();
      console.log('Filter params changed:', this.filterParams);

      this.selectedYear = this.filterParams?.year || new Date().getFullYear(); ; 
      if (!this.selectedYear || !this.filterParams?.fk_region || !this.filterParams?.fk_deu || !this.filterParams?.fk_work_category) { return; }
      const payload = {
        year: this.selectedYear,
        region_id: this.filterParams?.fk_region,
        deu_id: this.filterParams?.fk_deu,
        work_category_id: this.filterParams?.fk_work_category || -1,
      };

      console.log('Loading plans with payload:', payload);
      await this.loadPlans(payload);

      this.payload = { routine_plan_id: this.curPlan };
      await this.reloadData();
    },

    async copyItem(item) {
      let errDesc = '';
      let newId;
      const newItem = {
        fk_routine_plan: item.fk_routine_plan,
        fk_road: item.fk_road,
        fk_treatment_type: item.fk_treatment_type,
        fk_unit: item.fk_unit,
        cost: item.cost || 0,

        start_m: item.start_m,
        end_m: item.end_m,
        volume: item.volume,
        cycle_ratio: item.cycle_ratio,
      };

      try {
        const res = await this.addItem(newItem);
        newId = res?.routine_plan_details_id;
      } catch (err) {
        errDesc = err.message || this.$t('errors.unknown_error');
      }
      await savedMessage(errDesc, this.$t('messages.treatment_copied'), item.treatment_description);
      await this.parseFilterParams();

      this.highlightRow(newId)
    },
  },

  computed: {
    ...mapState({
    }),

    ...mapGetters('Planning', ['rmPlansDetailsFiltered', 'rmPlanDetailSummary', 'currentPlan']),

    tableData() {
      // Do not show anything until user selects a road
      return this.rmPlansDetailsFiltered(this.filterParams?.fk_road || undefined, this.filterParams?.fk_treatment);
    },

   headers() {
      return [
        { header: "stdCols.description", key: "treatment_type_description", sortable: true, width: 20 },
        { header: "inventory.start_distance_m", key: "start_m", sortable: true, num: true, width: 12 },
        { header: "inventory.end_distance_m", key: "end_m", sortable: true, num: true, width: 12 },
        { header: "label.volume", key: "volume", sortable: true, num: true, width: 10 },
        { header: "label.units", key: "unit_description", sortable: true, width: 8 },
        { header: "label.rate", key: "cost", sortable: true, num: true, digits: 2, width: 10 },
        { header: "label.cycle_ratio", key: "cycle_ratio", sortable: true, num: true, digits: 0, width: 10 },
        { header: "budget.total_cost", key: "total_cost", sortable: true, num: true, digits: 2, width: 13 },
      ]
    },

    planApproved() {
      return this.currentPlan(this.selectedYear).status == 2;
    },

    curPlan() {
      return this.currentPlan(this.selectedYear).id;
    },
 
    fields() {
      return [ 
        { name: 'fk_routine_plan', type: 'hidden', default: 'fk_routine_plan' },
        { name: 'fk_unit', type: 'hidden', default: 'fk_unit' },
        { name: 'fk_road', label: 'road_network.road', type: 'hidden', default: 'fk_road' },
        
        { name: 'fk_treatment_type', type: 'custom', component: 'TreatmentsDropdown', class: 'md-layout-item md-size-100', props: { routine: 1 } },
        { name: 'start_m', label: 'inventory.start_distance_m', type: 'number', class: 'md-layout-item md-size-40' },
        { name: 'end_m', label: 'inventory.end_distance_m', type: 'number', class: 'md-layout-item md-size-40' },
        { name: 'volume', label: 'volume', type: 'number', class: 'md-layout-item md-size-40' },
        { name: 'cost', label: 'stdCols.cost', type: 'number', class: 'md-layout-item md-size-40' },
        { name: 'cycle_ratio', label: 'cycle_ratio', type: 'number', class: 'md-layout-item md-size-40' },
      ]
    },

    validations() {
      return {
        fk_treatment: { required: true, numeric: true, min_value: 1 },
        start_m: { required: true, numeric: true },
        end_m: { required: true, numeric: true },
        volume: { required: true, decimal: true, min_value: 0.1 },
        cost: { required: true, decimal: true, min_value: 0.1 },
        cycle_ratio: { required: true, numeric: true },
      }
    },

    footerTable() {
      const total = this.rmPlanDetailSummary(this.filterParams?.fk_road || undefined, this.filterParams?.fk_treatment);

      return [
        this.$t('label.total'),
        `${this.$t('budget.works_count')}: ${numFormat(total.totalRecords, 0)}`,
        `${this.$t('budget.amount')}: ${numFormat(total.totalAmount)}`
      ];
    }
  },

  watch: {
    async step(newValue, oldValue) {
      if (newValue !== oldValue && newValue === 'second') {
        await this.parseFilterParams();
      };
    },

    filterParams: {
      async handler(value) {
        await this.parseFilterParams();
      },
      deep: true
    },
  }
}
</script>
<style lang="scss" scoped>
@import '@/mixins/BaseTable.scss';

// Bulletproof table styling that Vue Material cannot override
::v-deep .md-table {
  table-layout: fixed !important;
  width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  
  // Force table structure
  table {
    table-layout: fixed !important;
    width: 100% !important;
    border-collapse: separate !important;
  }
  
  // Column widths - carefully calculated to total 100%
  // Description: 23%, Start: 12%, End: 11%, Volume: 9%, Units: 8%, Rate: 8%, Cycle: 9%, Total: 11%, Actions: 9%
  colgroup {
    col:nth-child(1) { width: 23% !important; }
    col:nth-child(2) { width: 12% !important; }
    col:nth-child(3) { width: 11% !important; }
    col:nth-child(4) { width: 9% !important; }
    col:nth-child(5) { width: 8% !important; }
    col:nth-child(6) { width: 8% !important; }
    col:nth-child(7) { width: 9% !important; }
    col:nth-child(8) { width: 11% !important; }
    col:nth-child(9) { width: 9% !important; }
  }
  
  // Lock all cell widths with triple specification
  th, td {
    box-sizing: border-box !important;
    padding: 8px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    vertical-align: middle !important;
    border: none !important;
    
    &:nth-child(1) { 
      width: 23% !important; 
      max-width: 23% !important; 
      min-width: 23% !important; 
      text-align: left !important;
    }
    &:nth-child(2) { 
      width: 12% !important; 
      max-width: 12% !important; 
      min-width: 12% !important; 
      text-align: right !important;
    }
    &:nth-child(3) { 
      width: 11% !important; 
      max-width: 11% !important; 
      min-width: 11% !important; 
      text-align: right !important;
    }
    &:nth-child(4) { 
      width: 9% !important; 
      max-width: 9% !important; 
      min-width: 9% !important; 
      text-align: right !important;
    }
    &:nth-child(5) { 
      width: 8% !important; 
      max-width: 8% !important; 
      min-width: 8% !important; 
      text-align: left !important;
    }
    &:nth-child(6) { 
      width: 8% !important; 
      max-width: 8% !important; 
      min-width: 8% !important; 
      text-align: right !important;
    }
    &:nth-child(7) { 
      width: 9% !important; 
      max-width: 9% !important; 
      min-width: 9% !important; 
      text-align: right !important;
    }
    &:nth-child(8) { 
      width: 11% !important; 
      max-width: 11% !important; 
      min-width: 11% !important; 
      text-align: right !important;
    }
    &:nth-child(9) { 
      width: 9% !important; 
      max-width: 9% !important; 
      min-width: 9% !important; 
      text-align: center !important;
    }
  }
  
  // Header styling
  .md-table-head {
    background-color: #f8f9fa !important;
    border-bottom: 1px solid #dee2e6 !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    color: #495057 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    height: 44px !important;
  }
  
  .md-table-head-container {
    padding: 8px !important;
    display: flex !important;
    align-items: center !important;
    height: 100% !important;
    
    // Force header text alignment to match data
    &:nth-child(1) { justify-content: flex-start !important; } // Description
    &:nth-child(2) { justify-content: flex-end !important; }   // Start Distance  
    &:nth-child(3) { justify-content: flex-end !important; }   // End Distance
    &:nth-child(4) { justify-content: flex-end !important; }   // Volume
    &:nth-child(5) { justify-content: flex-start !important; } // Units
    &:nth-child(6) { justify-content: flex-end !important; }   // Rate
    &:nth-child(7) { justify-content: flex-end !important; }   // Cycle Ratio
    &:nth-child(8) { justify-content: flex-end !important; }   // Total Cost
    &:nth-child(9) { justify-content: center !important; }     // Actions
  }
  
  .md-table-head-label {
    font-size: 11px !important;
    font-weight: 600 !important;
    color: #6c757d !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    width: 100% !important;
  }
  
  // Force header alignment for each column specifically
  th {
    &:nth-child(1) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    &:nth-child(2) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(3) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(4) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(5) .md-table-head-container { justify-content: flex-start !important; text-align: left !important; }
    &:nth-child(6) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(7) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(8) .md-table-head-container { justify-content: flex-end !important; text-align: right !important; }
    &:nth-child(9) .md-table-head-container { justify-content: center !important; text-align: center !important; }
    
    &:nth-child(1) .md-table-head-label { text-align: left !important; }
    &:nth-child(2) .md-table-head-label { text-align: right !important; }
    &:nth-child(3) .md-table-head-label { text-align: right !important; }
    &:nth-child(4) .md-table-head-label { text-align: right !important; }
    &:nth-child(5) .md-table-head-label { text-align: left !important; }
    &:nth-child(6) .md-table-head-label { text-align: right !important; }
    &:nth-child(7) .md-table-head-label { text-align: right !important; }
    &:nth-child(8) .md-table-head-label { text-align: right !important; }
    &:nth-child(9) .md-table-head-label { text-align: center !important; }
  }
  
  // Data row styling
  tbody tr {
    border-bottom: 1px solid #f1f3f4 !important;
    
    &:hover {
      background-color: #f8f9fa !important;
    }
    
    td {
      padding: 12px 8px !important;
      font-size: 13px !important;
      color: #212529 !important;
      border: none !important;
      vertical-align: middle !important;
    }
  }
  
  // Action buttons styling
  .md-button {
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
    margin: 0 2px !important;
    
    .md-icon {
      font-size: 16px !important;
    }
  }
}

// Empty state styling
::v-deep .md-table-empty-state {
  .md-table-row td {
    text-align: center !important;
    padding: 60px 20px !important;
    color: #6c757d !important;
    font-size: 14px !important;
    border: none !important;
  }
}

// Footer styling
.footer-table {
  margin-top: 16px !important;
  
  .md-table-head {
    background-color: #e9ecef !important;
    border-top: 2px solid #dee2e6 !important;
    border-bottom: 1px solid #dee2e6 !important;
    font-weight: 700 !important;
    color: #495057 !important;
  }
  
  .md-table-head-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #495057 !important;
  }
}

// Override any Vue Material dynamic width calculations
::v-deep .md-table table,
::v-deep .md-table thead,
::v-deep .md-table tbody,
::v-deep .md-table tr,
::v-deep .md-table th,
::v-deep .md-table td {
  display: table !important;
  table-layout: fixed !important;
  width: 100% !important;
}

::v-deep .md-table th,
::v-deep .md-table td {
  display: table-cell !important;
}
</style>